import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from "react-router-dom";
import { Col, Row, Modal } from 'antd';
import { DOWNLOAD_URL } from '../config/api';
import firebase from "firebase/compat/app";
import Layout from './Layout';
import SearchBar from '../components/SearchBar';
import ClipboardJS from 'clipboard'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ClipLoader from "react-spinners/ClipLoader";
import PromoBox from "./common/PromoBox";
import { loadCatalog, findCategoryBySlug } from '../utils/stickerCatalog';

const isNumeric = (value) => /^\d+$/.test(value);

const Stickers = () => {
    const params = useParams();
    const history = useHistory();
    const premium = localStorage.getItem('premium');

    const [visible, setVisible] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [sticker, setSticker] = useState({});
    const [allStickers, setAllStickers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [sortBy, setSortBy] = useState(0); //0: Normal, 1: A-Z, 2: Z-A, 3: Most Popular, 4: Recently Released
    const [curPage, setCurPage] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageNum, setPageNum] = useState(24);
    const clipboard = new ClipboardJS('.Share');

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const catalog = await loadCatalog();
                setAllStickers(catalog.stickers);
                setCategories(catalog.categories);
            } catch(error) {

            } finally {
                setLoading(false);
            }
        };

        load();
        firebase.analytics().logEvent('cut_files_page_loaded');
    }, []);

    // The current category comes from the URL, so filters are shareable
    // (e.g. /stickers/teacher). Numeric params are legacy sticker share links.
    const curCategory = useMemo(() => {
        if (!params.slug || isNumeric(params.slug)) return null;
        return findCategoryBySlug(categories, params.slug) || null;
    }, [params.slug, categories]);

    // Legacy share links: /stickers/123 opens that sticker's modal.
    useEffect(() => {
        if (params.slug && isNumeric(params.slug) && allStickers.length > 0) {
            const legacySticker = allStickers.find(item => item.Id == params.slug);
            if (legacySticker) {
                setSticker(legacySticker);
                setModalVisible(true);
            }
        }
    }, [params.slug, allStickers]);

    useEffect(() => {
        setCurPage(0);
    }, [params.slug, searchText, pageNum, sortBy]);

    const filteredStickers = useMemo(() => {
        let list = allStickers;
        const query = searchText.trim().toLowerCase();

        if (query !== '') {
            // Search spans the entire catalog, not just the current category/page.
            list = list.filter(item =>
                (item.ImageName || '').toLowerCase().includes(query) ||
                (item.Tag || '').toLowerCase().includes(query)
            );
        } else if (curCategory) {
            list = list.filter(item => item.CategoryId == curCategory.Id);
        }

        list = [...list];
        if (sortBy == 1) {
            list.sort((a, b) => a.ImageName.toLowerCase().localeCompare(b.ImageName.toLowerCase()));
        } else if (sortBy == 2) {
            list.sort((a, b) => b.ImageName.toLowerCase().localeCompare(a.ImageName.toLowerCase()));
        } else if (sortBy == 3) {
            list.sort((a, b) => b.Downloads - a.Downloads);
        } else if (sortBy == 4) {
            list.sort((a, b) => (a.Created < b.Created ? 1 : a.Created > b.Created ? -1 : 0));
        } else if (query === '' && !curCategory) {
            list.sort((a, b) => a.CategorySortIndex - b.CategorySortIndex);
        }

        return list;
    }, [allStickers, curCategory, searchText, sortBy]);

    const totalPages = Math.max(1, Math.ceil(filteredStickers.length / pageNum));
    const pageStickers = filteredStickers.slice(curPage * pageNum, curPage * pageNum + pageNum);

    const goToCategory = (category) => {
        setSearchText('');
        history.push(category ? `/stickers/${category.Slug}` : '/stickers');
    };

    const handleCategoryClick = (e, category) => {
        e.preventDefault();
        goToCategory(category);
    };

    const handleMobileCategoryChange = (e) => {
        const slug = e.target.value;
        goToCategory(slug ? findCategoryBySlug(categories, slug) : null);
    };

    const handleStickerClick = (sticker) => {
        window.open('/sticker-detail/' + sticker.Id, "_self");
    }

    const handleSortByChange = (e) => {
        switch(e.target.value) {
            case "sort_by":
                setSortBy(0);
                break;
            case "name_asc":
                setSortBy(1);
                break;
            case "name_desc":
                setSortBy(2);
                break;
            case "most_popular":
                setSortBy(3);
                break;
            case "recently_released":
                setSortBy(4);
                break;
        }
    }

    const handlePerPageChange = (e) => {
        switch(e.target.value) {
            case "per_page":
                setPageNum(24);
                break;
            case "page_24":
                setPageNum(24);
                break;
            case "page_50":
                setPageNum(50);
                break;
            case "page_100":
                setPageNum(100);
                break;
        }
    }

    const handleDownloadSticker = (sticker, type) => {
        if(premium == 1) {
            if(type == 0) { //=====PNG
                window.open(`${DOWNLOAD_URL}/download.php?type=2&resource_id=${sticker.Id}`, "_blank")
            } else { //=====SVG
                window.open(`${DOWNLOAD_URL}/download.php?type=3&resource_id=${sticker.Id}`, "_blank")
            }
        } else {
            window.open('/payment', '_blank');
        }
    }

    const handleLeftArrowClick = () => {
        if(curPage > 0) {
          setCurPage(curPage - 1);
        }
    }
    
    const handleRightArrowClick = () => {
        if(curPage < totalPages - 1) {
            setCurPage(curPage + 1);
        }
    }

    const handleSearch = (event) => {
        setSearchText(event.target.value);
    }

    const renderPagination = () => (
        <div className="pagination">
            <FaChevronLeft color="#c1c6c7" onClick={() => handleLeftArrowClick()} className="page-arrow-left" />
            <div className="page-text">
                {(curPage + 1) + " / " + totalPages}
            </div>
            <FaChevronRight color="#c1c6c7" onClick={() => handleRightArrowClick()} className="page-arrow-right"/>
        </div>
    );

    return (
        <Layout>
            <div role="main" className="main" style={{ paddingTop: 140 }}>
                <div className="container">
                    <PromoBox />
                    <div className="row mb-5">
                        <h2 className="font-weight-bold text-9 center primary-color font-primary-bold">Cut Files</h2>
                    </div>
                    <div className="row">
                        <aside className="sidebar col-md-4 col-lg-3 order-1 order-md-1 d-none d-md-block">
                            <div style={{display: 'flex'}}>
                                <span className="text-4 center font-secondary" style={{ padding: '1rem 1.5rem'}}>Filter by</span>
                                {renderPagination()}
                            </div>
      
                            <hr />
                            <div className="accordion accordion-default accordion-toggle accordion-style-1" role="tablist">
                                <div className="card">
                                    <div className="card-header accordion-header" role="tab" id="categories">
                                        <h5 className="mb-0">
                                            <a className="collapse" data-toggle="collapse" data-target="#toggleCategories" aria-expanded="false" aria-controls="accordionDefaultSmCollapse2" style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setVisible(!visible)}>
                                                <span>Categories</span>
                                                <img src={`/assets/img/${visible ? 'icons8-minus-14' : 'icons8-plus-math-14'}.png`} style={{ opacity: 0.4 }}/>
                                            </a>
                                        </h5>
                                    </div>
                                    <div id="toggleCategories" className="accordion-body collapse show" role="tabpanel" aria-labelledby="categories">
                                        <div className="card-body">
                                            <ul className="list list-unstyled mb-0">
                                                <li key="all">
                                                    <a href="/stickers" className={!curCategory ? 'active' : ''} onClick={(e) => handleCategoryClick(e, null)}>
                                                        All{allStickers.length > 0 ? ` \u00b7 ${allStickers.length.toLocaleString()}` : ''}
                                                    </a>
                                                </li>
                                                {categories.map((item) => (
                                                    <li key={item.Id}>
                                                        <a href={`/stickers/${item.Slug}`} className={curCategory && curCategory.Id == item.Id ? 'active' : ''} onClick={(e) => handleCategoryClick(e, item)}>
                                                            {item.Name} &middot; {item.Count}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </aside>
                        <div className="col-md-8 col-lg-9 order-2 order-md-2 mb-5 mb-md-0">
                            <div className="row">
                                <div className="col-12 col-lg-6 mb-3 order-1 order-lg-4">
                                    <SearchBar
                                        handleSearch={handleSearch}
                                        value={searchText}
                                        placeholder={allStickers.length > 0 ? `Search all ${allStickers.length.toLocaleString()} cut files...` : 'Search cut files...'}
                                    />
                                </div>
                                <div className="col-12 d-md-none mb-3 order-2">
                                    <div className="custom-select-1">
                                        <select className="form-control border" value={curCategory ? curCategory.Slug : ''} onChange={handleMobileCategoryChange}>
                                            <option value="">All Categories{allStickers.length > 0 ? ` \u00b7 ${allStickers.length.toLocaleString()}` : ''}</option>
                                            {categories.map((item) => (
                                                <option key={item.Id} value={item.Slug}>{item.Name} &middot; {item.Count}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-6 col-lg-3 mb-3 mb-sm-0 order-3 order-lg-1">
                                    <div className="custom-select-1">
                                        <select className="form-control border" onChange={handlePerPageChange}>
                                            <option value="per_page">Per page</option>
                                            <option value="page_24">24</option>
                                            <option value="page_50">50</option>
                                            <option value="page_100">100</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-6 col-lg-3 mb-3 mb-sm-0 order-4 order-lg-2">
                                    <div className="custom-select-1">
                                        <select className="form-control border" onChange={handleSortByChange}>
                                            <option value="sort_by">Sort by</option>
                                            <option value="name_asc">Name A-Z</option>
                                            <option value="name_desc">Name Z-A</option>
                                            <option value="most_popular">Most popular</option>
                                            <option value="recently_released">Recently released</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {searchText.trim() !== '' &&
                                <p className="mb-2" style={{ fontSize: 15 }}>
                                    {filteredStickers.length.toLocaleString()} {filteredStickers.length === 1 ? 'result' : 'results'} for "{searchText.trim()}" across all categories
                                </p>
                            }

                            <div className="center-horizontal">
                                <ClipLoader color="#25a66a" loading={loading} size={60} />
                            </div>

                            <div className="row">
                                {pageStickers.map((item) => (
                                    <div className="col-6 col-lg-4" key={item.Id}>
                                        <div className="item-box" onClick={() => handleStickerClick(item)} onContextMenu={(e) => e.preventDefault()}>
                                            <div className='sticker-image'>
                                                <img src={item.ImageUrl} className="img-fluid" alt={item.ImageName} />
                                            </div>
                                            <div className="item-bottom-box">
                                                <div className="sticker-item-title">
                                                    {item.ImageName}
                                                </div>
                                                {premium != 1 && <img className="download" src="/assets/img/new.png" />}
                                            </div>
                                        </div>
                                    </div>                                    
                                ))}
                            </div>

                            <div className="pagination float-right">
                                <FaChevronLeft color="#c1c6c7" onClick={() => handleLeftArrowClick()} className="page-arrow-left" />
                                <div className="page-text">
                                    {(curPage + 1) + " / " + totalPages}
                                </div>
                                <FaChevronRight color="#c1c6c7" onClick={() => handleRightArrowClick()} className="page-arrow-right"/>
                            </div>
                        </div>
                    </div>
                </div>
                <Modal
                    visible={modalVisible}
                    title={<div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{sticker.ImageName}</span><span>X</span></div>}
                    footer={null}
                    onCancel={() => { setModalVisible(false) }} 
                    centered
                    cancelButtonProps={{ style: { display: 'none' } }}
                >
                    <Row style={{justifyContent: 'center'}}>
                        <img src={sticker.ImageUrl} width="180" />
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <button className="tp-caption btn btn-primary btn-primary-override font-primary-regular text-center" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleDownloadSticker(sticker, 0)}>Download PNG</button>
                        </Col>
                        <Col span={12}>
                            <button className="tp-caption btn btn-primary btn-primary-override font-primary-regular text-center" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleDownloadSticker(sticker, 1)}>Download SVG</button>
                        </Col>
                    </Row>
                    <p style={{ fontSize: 18, lineHeight: 1.5, marginTop: 10 }}>Tags: {sticker.Tag}</p>
                    <Row>
                        <Col span={5}>Share Link:</Col>
                        <Col span={16}><input id="share-link" className="share-input" value={"https://crafterspro.net/stickers/" + sticker.Id} onChange={(e) => {}} /></Col>
                        <Col span={3}><img className="Share" src="/assets/img/icons8-copy-30.png" data-clipboard-target="#share-link"/></Col>
                    </Row>
                </Modal>
            </div>
        </Layout>
    );
}

export default Stickers;
